import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import Radio from '@components/v2/form/Radio/Radio';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, FormControlLabel, Stack } from '@mui/material';
import { Controller, FormProvider } from 'react-hook-form';
import { useBlockRefreshIf } from '../../../../../../hooks/useBlockRefreshIf';
import LangEnum from '../../../../../../lang/LangEnum';
import OrderBookOptionRenderer from '../../../../../order-book/OrderBookOptionRenderer';
import { ListOrdersV2Output } from '../../../../../order-book/types/orderBook.types';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useOrderContext } from '../../../../contexts/Order.context';
import { CopyModeOnItemsConflictEnum } from '../../enums/CopyModeOnItemsConflict.enum';
import { CopyOrderProductsToExistingOrderFormProps } from './CopyOrderProductsToExistingOrderForm.interfaces';
import RadioGroupItemLabel from './components/RadioGroupItemLabel/RadioGroupItemLabel';

const COPY_MODE_OPTIONS = [
  {
    value: CopyModeOnItemsConflictEnum.REPLACE_ORIGINAL,
    title: LangEnum.COPY_ORDER_PRODUCTS_REPLACE_EXISTING,
    description: LangEnum.COPY_ORDER_PRODUCTS_REPLACE_EXISTING_DESCRIPTION,
  },
  {
    value: CopyModeOnItemsConflictEnum.SKIP_DUPLICATES,
    title: LangEnum.COPY_ORDER_PRODUCTS_ONLY_NEW,
    description: LangEnum.COPY_ORDER_PRODUCTS_ONLY_NEW_DESCRIPTION,
  },
];

const CopyOrderProductsToExistingOrderForm = ({
  source,
  form,
  handleSubmit,
}: CopyOrderProductsToExistingOrderFormProps) => {
  useBlockRefreshIf({ condition: form.formState.isSubmitting });
  const { translate } = useTranslation();
  const { order } = useOrderContext();

  const {
    control,
    formState: { isSubmitting, errors },
  } = form;

  const getOrderOptionLabel = (existingOrder: ListOrdersV2Output) => {
    const customerName =
      existingOrder.customer?.commercialName ??
      translate(LangEnum.ORDER_BOOK_ORDER_WITHOUT_CUSTOMER);

    return `${translate(LangEnum.GLOBAL_ORDER)} ${existingOrder.code} (${customerName})`;
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack sx={{ gap: 2 }}>
          <Stack sx={{ gap: 1 }}>
            <Controller
              control={control}
              name="orders"
              render={({ field }) => (
                <AsyncAutocomplete<ListOrdersV2Output, false>
                  name={field.name}
                  label={translate(LangEnum.GLOBAL_EXISTING_ORDERS)}
                  value={field.value[0]?.id ?? null}
                  initialData={field.value}
                  disabled={isSubmitting}
                  error={!!errors.orders?.message}
                  helperText={errors.orders?.message}
                  source={source}
                  getOptionLabel={getOrderOptionLabel}
                  formatOptions={(orders) =>
                    orders.filter((existingOrder) => existingOrder.id !== order?.id)
                  }
                  renderOption={(existingOrder) => (
                    <OrderBookOptionRenderer order={existingOrder} />
                  )}
                  onChange={(_, orders) => field.onChange(orders)}
                />
              )}
            />
            <Box>
              <Controller
                control={control}
                name="removeFromOrigin"
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ ml: -0.75 }}
                    label={translate(LangEnum.COPY_ORDER_PRODUCTS_REMOVE_FROM_CURRENT_ORDER)}
                    control={
                      <Checkbox
                        name={field.name}
                        checked={field.value}
                        disabled={isSubmitting}
                        onChange={field.onChange}
                      />
                    }
                  />
                )}
              />
            </Box>
          </Stack>
          <Box sx={{ pl: 0.5 }}>
            <Controller
              control={control}
              name="copyModeOnItemsConflict"
              render={({ field }) => (
                <Stack sx={{ gap: 1.5 }}>
                  <Typography variant="overline" color="text.secondary">
                    {translate(LangEnum.COPY_ORDER_PRODUCTS_ON_DUPLICATE_BEHAVIOR)}
                  </Typography>
                  <Stack
                    sx={{ gap: 1.5 }}
                    role="radiogroup"
                    aria-label={translate(LangEnum.COPY_ORDER_PRODUCTS_ON_DUPLICATE_BEHAVIOR)}
                  >
                    {COPY_MODE_OPTIONS.map(({ value, title, description }) => (
                      <Radio
                        key={value}
                        name={field.name}
                        value={value}
                        checked={field.value === value}
                        disabled={isSubmitting}
                        sx={{ ml: -0.75 }}
                        onChange={() => field.onChange(value)}
                        label={<RadioGroupItemLabel title={title} description={description} />}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            />
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default CopyOrderProductsToExistingOrderForm;
